// The « Police » of « Légendes IA » (owner, 2026-10-15): « I want to see the
// actual fonts, how they look — not only the name ». A native <select> draws
// its options in the system font (macOS), so every font here is drawn in its
// own face, with a line of the album's own words. The first row is the model's
// own font, named and drawn — choosing it really means « keep the model's ».
import { useEffect, useRef, useState } from 'react';
import { EDITOR_TEXT_FONTS, fontName } from '@/lib/editorFonts';

export interface CaptionFontPickerProps {
  /** '' = the model's own font. */
  value: string;
  onChange: (family: string) => void;
  /** The font the model's caption boxes use (drawn on the first row). */
  modelFamily: string;
  /** A line to draw each font with (the client's own words when there are some). */
  sample: string;
  tokens: { ink: string; muted: string; panel: string; line: string; chip: string; gold: string; goldInk: string };
}

export function CaptionFontPicker({ value, onChange, modelFamily, sample, tokens: T }: CaptionFontPickerProps) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc, true);
    // The chosen row in view.
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc, true); };
  }, [open]);

  const rows = [{ family: '', label: `Police du modèle · ${fontName(modelFamily)}`, draw: modelFamily }, ...EDITOR_TEXT_FONTS.map((f) => ({ family: f.family, label: f.label, draw: f.family }))];
  const current = rows.find((r) => r.family === value) ?? rows[0];
  const pick = (family: string) => { onChange(family); setOpen(false); };

  return (
    <div ref={box} className="relative">
      <button
        type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left focus-visible:outline focus-visible:outline-2"
        style={{ border: `1px solid ${open ? T.gold : T.line}`, background: T.panel, color: T.ink, outlineColor: T.gold }}
      >
        <span className="min-w-0">
          <span className="block text-[11px]" style={{ color: T.muted }}>{value ? fontName(value) : current.label}</span>
          <span className="block truncate text-lg leading-snug" style={{ fontFamily: current.draw }}>{sample}</span>
        </span>
        <span aria-hidden className="shrink-0 text-xs" style={{ color: T.muted }}>{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <ul
          ref={list} role="listbox" aria-label="Police des légendes"
          className="mt-1 max-h-[22rem] overflow-y-auto rounded-xl shadow-lg py-1"
          style={{ background: T.panel, border: `1px solid ${T.line}` }}
        >
          {rows.map((r, i) => {
            const on = r.family === value;
            return (
              <li key={r.family || 'model'} role="option" aria-selected={on}>
                <button
                  type="button" onClick={() => pick(r.family)}
                  className="w-full text-left px-3 py-2 focus-visible:outline-none"
                  style={{ background: on ? `${T.gold}22` : 'transparent', borderBottom: i === 0 ? `1px solid ${T.line}` : undefined }}
                  onMouseEnter={(e) => { if (!on) e.currentTarget.style.background = T.chip; }}
                  onMouseLeave={(e) => { if (!on) e.currentTarget.style.background = 'transparent'; }}
                  onFocus={(e) => { if (!on) e.currentTarget.style.background = T.chip; }}
                  onBlur={(e) => { if (!on) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span className="flex items-center justify-between text-[11px]" style={{ color: on ? T.goldInk : T.muted }}>
                    {r.label}{on ? <span aria-hidden>✓</span> : null}
                  </span>
                  <span className="block truncate text-xl leading-snug" style={{ fontFamily: r.draw, color: T.ink }}>{sample}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
